import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Radar } from "lucide-react";

type Msg = { role: "user" | "assistant"; content: string; citations?: string[] };

const GREETING: Msg = {
  role: "assistant",
  content:
    "NEXUS online — the CARIO INTEL assistant, running on Grok with live web + X search.\n\nAsk me anything about the platform: modules, data sources, pricing, legality, security, onboarding — or ask me to research a company or topic live.",
};

const SUGGESTIONS = [
  "What can CARIO INTEL actually find?",
  "How does pricing and the free trial work?",
  "Which data sources do you use?",
  "Is this legal and GDPR-safe?",
];

function renderInline(text: string, key: number) {
  // minimal markdown: **bold** and [label](url)
  const nodes: React.ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)]+)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1]) nodes.push(<strong key={`${key}-b${i++}`}>{m[1]}</strong>);
    else {
      const label = m[2] ?? m[4]!;
      const href = m[3] ?? m[4]!;
      nodes.push(
        <a key={`${key}-a${i++}`} href={href} target="_blank" rel="noreferrer" className="text-cyan underline underline-offset-2">
          {label}
        </a>,
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function SiteChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    // Avoid auto-focus on touch devices: the keyboard + iOS zoom breaks the layout.
    const isTouch = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
    if (!isTouch) inputRef.current?.focus();
  }, [open, busy]);

  // Lock background scroll while the sheet is open on mobile.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (!text || busy) return;
    const next: Msg[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setErr(null);
    setBusy(true);
    try {
      const r = await fetch("/api/public/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: next.filter((m) => m !== GREETING).map((m) => ({ role: m.role, content: m.content })).slice(-20),
        }),
      });
      const data = (await r.json().catch(() => null)) as { reply?: string; citations?: string[]; error?: string } | null;
      if (!r.ok || !data?.reply) {
        setErr(data?.error ?? "The assistant is unavailable right now. Please try again.");
        return;
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply!, citations: data.citations ?? [] }]);
    } catch {
      setErr("Network error — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Open the CARIO INTEL assistant"
          className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-50 flex max-w-[calc(100vw-2rem)] items-center gap-2 rounded-full border border-cyan/40 bg-panel/90 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-cyan shadow-lg backdrop-blur transition hover:bg-panel sm:bottom-5 sm:right-5"
        >
          <MessageSquare className="h-4 w-4" />
          Ask NEXUS
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-label="CARIO INTEL assistant"
          className="fixed inset-0 z-50 flex h-[100dvh] w-full flex-col overflow-hidden border-panel-border bg-background shadow-2xl sm:inset-auto sm:right-5 sm:bottom-5 sm:h-[600px] sm:max-h-[85vh] sm:w-[400px] sm:rounded-xl sm:border sm:bg-background/95 sm:backdrop-blur"
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-panel-border px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:pt-3">
            <Radar className="h-4 w-4 text-cyan" />
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">NEXUS assistant</div>
              <div className="truncate text-[11px] text-muted-foreground">Live web + X research · legal OSINT only</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close assistant" className="ml-auto grid h-9 w-9 shrink-0 place-items-center rounded-md text-muted-foreground hover:text-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={
                    m.role === "user"
                      ? "max-w-[85%] break-words rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground"
                      : "max-w-[92%] break-words text-sm leading-relaxed text-foreground"
                  }
                >
                  {m.content.split("\n").map((line, li) => (
                    <p key={li} className={line.trim() ? "mb-1" : "mb-2"}>
                      {renderInline(line, li)}
                    </p>
                  ))}
                  {!!m.citations?.length && (
                    <div className="mt-2 space-y-1 border-t border-panel-border pt-2">
                      {m.citations.map((c, ci) => (
                        <a key={ci} href={c} target="_blank" rel="noreferrer" className="block truncate font-mono text-[10px] text-muted-foreground hover:text-cyan">
                          {ci + 1}. {c}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && <div className="animate-pulse font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">NEXUS researching…</div>}
            {err && <div className="rounded-md border border-danger/40 bg-danger/10 p-2 text-xs text-danger">{err}</div>}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="rounded-full border border-panel-border bg-panel/40 px-3 py-1 text-left text-[11px] text-muted-foreground hover:text-foreground">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-end gap-2 border-t border-panel-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-3">
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder="Ask about features, pricing, sources…"
              className="max-h-28 min-h-[42px] w-full min-w-0 flex-1 resize-none rounded-md border border-panel-border bg-background/60 px-3 py-2 text-base outline-none focus:border-cyan/50 sm:text-sm"
            />
            <button
              onClick={() => void send()}
              disabled={busy || !input.trim()}
              aria-label="Send message"
              className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-md bg-primary text-primary-foreground disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
